import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { CenterFrameLayout } from 'Layouts';
import { fetchSubscriptions, fetchSubscriptionStatus } from 'State/Subscriptions/SubscriptionsActions';
import { verifyGiftCode, redeemGift } from 'Api/AccountApi';
import { getEntitlements } from 'State/User/UserActions';
import { onSubmitReactFormError } from 'Utils/FormValidation';
import { GIFTING_QUERY_PARAM } from 'Utils/Constants';
import { GiftCardInfo } from 'Features/GiftCardInfo';
import { SmartLink } from 'Components/SmartLink';
import RedeemGiftCardForm from 'Forms/RedeemGiftCardForm';
import { ShippingForm } from 'Forms/ShippingForm';
import ZipCodeForm from 'Forms/ZipCodeForm';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { RedeemGiftFinePrint, RedeemGiftTrialistFinePrint, RedeemGiftIAPFinePrint } from 'Components/FinePrint';
import giftPlans from 'Config/GiftPlans.json';
import './RedeemGiftCardContainer.css';

export const RedeemGiftCardContainer = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [giftData, setGiftData] = useState(null);
  const [giftCode, setGiftCode] = useState(null);
  const [showShippingForm, setShippingForm] = useState(false);
  const [showZipCodeForm, setZipCodeForm] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [zipCodeFormValues, setZipcodeFormValues] = useState({});
  const {
    routes: { signin, giftCardRedeemConfirmation },
    defaultConfig: { rootDomain },
  } = useSelector((s) => s.appConfig);
  const { isAuthenticated } = useSelector((s) => s.user);
  const { isIAPUser, isTrialistUser } = useSelector((s) => s.subscription);

  useEffect(() => {
    setShippingForm(giftData && giftData.hasMerchandise);
    setZipCodeForm(giftData && !giftData.hasMerchandise);
  }, [giftData]);

  useEffect(() => {
    dispatch(fetchSubscriptions());
    dispatch(fetchSubscriptionStatus());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      trackEvent(TRACKING_ENUMS.EVENT_REDEEM_STARTED);
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return <Navigate replace to={`${signin}?${GIFTING_QUERY_PARAM}`} />;
  }

  const handleGiftCodeSubmit = async ({ giftCode }) => {
    try {
      const giftData = await verifyGiftCode(giftCode);
      if (giftData && giftData.packageId) {
        const { packageId, productPrice, productSku } = giftData;
        setGiftData(giftData);
        setGiftCode(giftCode);
        trackEvent(TRACKING_ENUMS.EVENT_REDEEM_APPLIED, {
          page_plan_name: (giftPlans[packageId] && giftPlans[packageId].title) || '',
          page_plan_price: parseFloat(productPrice),
          page_plan_id: productSku,
        });
      }
    } catch (error) {
      trackEvent(TRACKING_ENUMS.EVENT_REDEEM_ERROR, {
        page_error_description: error,
        page_error_type: 'server-side',
      });
      return onSubmitReactFormError(error || 'Please enter a valid gift PIN code.');
    }
  };

  const onSubmitToRedeem = async (shippingAddress) => {
    setLoading(true);

    const billingZipCode = zipCodeFormValues?.zipCode;
    if (shippingAddress || billingZipCode) {
      try {
        const { transactionId } = await redeemGift({
          pin: giftCode,
          shippingAddress,
          billingZipCode,
        });

        await dispatch(getEntitlements());
        trackEvent(TRACKING_ENUMS.EVENT_REDEEM_COMPLETED, {
          page_order_id: transactionId,
          page_plan_name: (giftPlans[giftData.packageId] && giftPlans[giftData.packageId].title) || '',
          page_plan_price: parseFloat(giftData.productPrice),
          page_plan_id: giftData.productSku,
        });
        navigate(giftCardRedeemConfirmation);
      } catch (error) {
        setLoading(false);
        return onSubmitReactFormError(error);
      }
    }
  };

  return (
    <CenterFrameLayout title="Redeem Your Gift Code" showLoader={false} customClass="redeemGiftCardContainer">
      {isTrialistUser && (
        <div className="trialistMsgWrapper">
          <RedeemGiftTrialistFinePrint className="noRedeemMsg" />
          <SmartLink className="redeemWatchNowBtn" text="Watch Fox Nation" linkUrl={rootDomain} reload />
        </div>
      )}
      {isIAPUser && <RedeemGiftIAPFinePrint className="noRedeemMsg" />}
      <div className="centerForm">
        <RedeemGiftCardForm
          isDisabled={isTrialistUser || isIAPUser}
          onFormSubmit={handleGiftCodeSubmit}
          onChangeGiftCode={() => setGiftData(null)}
        />
        {!giftData && <RedeemGiftFinePrint className="disclaimer" />}
        <GiftCardInfo giftId={giftData && giftData.packageId} />
        {showShippingForm && (
          <div className="redeemYouGiftForm">
            <div className="redeemYouGiftFormTitle">Where should we mail your gift merchandise?</div>
            <ShippingForm onFormSubmit={onSubmitToRedeem} isLoading={isLoading} />
          </div>
        )}
        {showZipCodeForm && (
          <ZipCodeForm onFormSubmit={onSubmitToRedeem} isLoading={isLoading} setFormValues={setZipcodeFormValues} />
        )}
      </div>
    </CenterFrameLayout>
  );
};
